import {
  BookmarkIcon,
  CheckIcon,
  CreationIcon,
  DualEllipsesIcon,
  PinFilledIcon,
  PinIcon,
  RightArrowIcon,
} from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import Link, { LinkProps } from 'next/link';
import * as React from 'react';

import { Button } from '../button';
import {
  Description,
  IconText,
  ImageTag,
  Progress,
  Stage,
  StageProps,
  Title,
  Wrapper,
} from './components';
const { useState, useEffect } = React;
export interface LessonProps extends LinkProps {
  tag?: {
    label: string;
    color: string;
    value: string;
  };
  title: string;
  progress?: {
    total: number;
    completed: number;
  };
  imageUrl: string;
  // TODO: remove the optional param of imageAlt after adding this in all modules
  imageAlt?: string;
  className?: string;
  isLearning?: boolean;
  creation?: 'unlocked' | 'started' | string;
  description?: string;
  stage?: StageProps;
  bookmarksCount?: number;
  translations: {
    // creation_unlocked: string;
    // creation_in_use: string;
    continue_learning: string;
    start_creating: string;
    bookmarked_flashcards: string;
    start_learning: string;
  };
  onClick?: () => void;
  onCreationClick?: () => void;
  pinned?: boolean;
  handlePinLesson?: (slug: string, pin: boolean, name: string) => void;
  slug?: string;
  showPin?: boolean;
}

export const Lesson = ({
  tag,
  title,
  progress,
  imageUrl,
  className,
  isLearning,
  creation,
  description,
  stage,
  bookmarksCount,
  translations,
  href,
  onCreationClick,
  imageAlt,
  onClick,
  pinned,
  handlePinLesson,
  slug,
  showPin = true,
}: LessonProps) => {
  const [pin, setPin] = useState(false);
  useEffect(() => {
    pinned && setPin(pinned);
  }, [pinned]);
  return (
    <Link href={href}>
      <a className={classNames({ 'w-304': true, [className || '']: !!className })}>
        <Wrapper onClick={onClick}>
          <div className="relative">
            <ImageTag tag={tag} src={imageUrl} alt={imageAlt} className="mb-16" />
            {showPin && (
              <Tippy
                className="w-auto h-auto bg-gray-999"
                placement="bottom"
                content={pin ? 'Unpin lesson from sidebar' : 'Pin lesson to sidebar'}
              >
                <div className="absolute right-8 top-8 bg-white shadow-sm rounded-sm">
                  <button
                    className="p-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handlePinLesson?.(slug!, !pin, title);
                      setPin(!pin);
                    }}
                  >
                    {pin ? (
                      <PinFilledIcon className="text-gray-500" />
                    ) : (
                      <PinIcon className="text-gray-500" />
                    )}
                  </button>
                </div>
              </Tippy>
            )}
          </div>

          <div className="mx-16">
            <Title className="mb-14">{title}</Title>
            {progress ? (
              <Progress completed={progress.completed} total={progress.total} className="mb-20" />
            ) : null}

            {description ? <Description className="mb-18">{description}</Description> : null}
          </div>
          {!isLearning && !bookmarksCount ? (
            <Button
              onClick={onCreationClick}
              className="mx-16 mb-8"
              style={{ paddingTop: '10px', paddingBottom: '10px' }}
              tabIndex={-1}
              iconRight={<RightArrowIcon className="ml-auto" />}
            >
              {translations.start_creating}
            </Button>
          ) : null}
          {!isLearning && !creation && !bookmarksCount ? (
            <button
              className="mx-8 pl-14 mb-8 text-blue-500 text-left text-xs font-medium"
              tabIndex={-1}
            >
              {translations.start_learning}
            </button>
          ) : null}

          {isLearning ? (
            <Button
              status="tertiary"
              className="mx-8 mb-8 "
              tabIndex={-1}
              iconRight={<RightArrowIcon className="ml-auto" />}
            >
              {isLearning ? translations.continue_learning : ''}
            </Button>
          ) : null}
          {bookmarksCount ? (
            <Button
              status="tertiary-gray"
              className="mx-8 mb-8"
              iconLeft={<BookmarkIcon className="ml-auto" />}
            >
              {`${bookmarksCount} ${translations.bookmarked_flashcards}`}
            </Button>
          ) : null}
        </Wrapper>
      </a>
    </Link>
  );
};

export interface NewLessonProps extends LinkProps {
  translations: {
    new_lesson_card_title: string;
    new_lesson_cta_label: string;
  };
  className?: string;
}

export const NewLesson = ({ href, className, translations }: NewLessonProps) => {
  return (
    <Link href={href}>
      <a className={className}>
        <Wrapper
          className={classNames({
            'w-304 flex flex-col justify-between h-full p-24 bg-primary-100 border-primary-200':
              true,
          })}
        >
          <h3 className="text-primary-700 font-bold text-lg mb-32">
            {translations.new_lesson_card_title}
          </h3>
          <DualEllipsesIcon />
          <Button
            status="ghost"
            className="w-full mt-auto"
            tabIndex={-1}
            style={{ padding: 0 }}
            iconRight={<RightArrowIcon className="ml-auto" />}
          >
            {translations.new_lesson_cta_label}
          </Button>
        </Wrapper>
      </a>
    </Link>
  );
};
export interface LegalLessonProps {
  title: string;
  imageUrl: string;
  className?: string;
  translations: {
    continue_learning: string;
    start_creating: string;
    bookmarked_flashcards: string;
    start_learning: string;
  };
  onClick?: () => void;
  onCreationClick?: () => void;
  onLessonClick?: () => void;
  slug?: string;
}

export const LegalLesson = ({
  title,
  imageUrl,
  className,
  translations,
  onCreationClick,
  onLessonClick,
}: LegalLessonProps) => {
  return (
    <div className={classNames({ 'w-304': true, [className || '']: !!className })}>
      <Wrapper onClick={onCreationClick}>
        <div className="relative">
          <ImageTag src={imageUrl} className="mb-16" />
        </div>

        <div className="mx-16">
          <Title className="mb-14">{title}</Title>
        </div>

        <Button
          onClick={onCreationClick}
          className="mx-8 mb-8"
          style={{ paddingTop: '10px', paddingBottom: '10px' }}
          tabIndex={-1}
          iconRight={<RightArrowIcon className="ml-auto" />}
        >
          Generate contract
        </Button>

        {/* <button
          className="mx-8 pl-14 mb-8 text-blue-500 text-left text-xs font-medium"
          tabIndex={-1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            onLessonClick?.();
          }}
        >
          {translations.start_learning}
        </button> */}
      </Wrapper>
    </div>
  );
};
